export enum FilterParamNames {
  assetFileKind = 'assetFileKind',
  type = 'type',

  tagIdAnd = 'tagIdAnd',
  tagIdOr = 'tagIdOr',
  tagIdNot = 'tagIdNot',

  smartTagIdAnd = 'smartTagIdAnd',
  smartTagIdOr = 'smartTagIdOr',
  smartTagIdNot = 'smartTagIdNot',

  /** Person filters */
  personIdAnd = 'pIdAnd',
  personIdOr = 'pIdOr',

  /** Imported keywords */
  importedKeywordAnd = 'impKeyAnd',
  importedKeywordOr = 'impKeyOr',
  importedKeywordNot = 'importedKeywordNot',

  uploaderId = 'uploaderId',

  cameraMake = 'camMake',
  cameraModel = 'camModel',

  creator = 'cr',

  copyright = 'cop',

  color = 'color',
  colorThreshold = 'colorThreshold',

  ext = 'ext',

  source = 'source',

  // location filters
  // these filters are independent - user can select any country, any city and any state, even if selected city does not belong to selected country
  // in the future we will probably make one "Location" filter, which will be a combination of country, city and state
  country = 'ct',
  city = 'city',
  state = 'st',

  orientation = 'ort',
  videoFrameRate = 'vfr',
  videoAspectRatio = 'var',
  audioCoding = 'ac',
  audioSampleRate = 'asr',

  bookmarked = 'bookmarked',
  isOnBoards = 'isOnBoards',
  hasOpenComments = 'hasOpenComments',
  hasMultipleVersions = 'hasMultipleVersions',
  untagged = 'untagged',

  cfIdAnd = 'cfIdAnd-',
  cfIdOr = 'cfIdOr-',
  cfIdNot = 'cfIdNot-',
  cfTextIs = 'cfTextIs-',
  cfTextPrefix = 'cfTextPrefix-',
  cfTextOr = 'cfTextOr-',
  cfTextNot = 'cfTextNot-',
  cfNotSet = 'cfNotSet-',
  cfDateStart = 'cfDateStart-',
  cfDateEnd = 'cfDateEnd-',

  recordedDateStart = 'recordedDateStart',
  recordedDateEnd = 'recordedDateEnd',

  uploadedDateStart = 'uploadedDateStart',
  uploadedDateEnd = 'uploadedDateEnd',

  modifiedDateStart = 'modifiedDateStart',
  modifiedDateEnd = 'modifiedDateEnd',

  takenDateStart = 'tds',
  takenDateEnd = 'tde',

  libraryId = 'libraryId',
  boardId = 'boardId',
  onlyInLibraries = 'onlyInLibraries',

  locationBoards = 'boards',
  locationLibraries = 'libraries',

  similarToImage = 'similarToImage',
  similarTo = 'similarTo',
}

export const FilterParamNamesArray = Object.values(FilterParamNames);

export type QueryType = Partial<Record<QueryParamNames, string | string[] | number | boolean | null>>;

export enum QueryParamNames {
  Search = 'q',
  /** Opens the collection link editor overlay on `/showcases`. */
  Showcase = 'showcase',
  workspaceName = 'workspaceName',
  workspaceId = 'wsid',
  isFlattenedView = 'isFlatView',
  /** tab in private board view (e.g. content vs canvases). The asset modal uses {@link amTab} instead so the two don't collide. */
  tab = 'tab',

  /** Tab in asset modal. Prefixed `am-` because the asset modal can open on top of pages that own their own `tab` param. */
  amTab = 'am-tab',

  boardDetailsTab = 'board-details-tab',

  /** Determine whether to open versions panel, should be true or false */
  versions = 'versions',

  /**
   * This is active timestamp of video asset - time on which video is paused (used e.g. to show annotations)
   */
  timestamp = 'ts',

  /** ID of the second clip shown by the asset comparison viewer. */
  comparisonClipId = 'cmp',

  /** Asset comparison layout: `sbs` or `slider`. */
  comparisonMode = 'cmpMode',

  /** Active comparison panel: `0` for left or `1` for right. */
  comparisonActivePanel = 'cmpActive',

  /** Whether playback or image movement is linked across comparison panels. */
  comparisonLinked = 'cmpLink',

  /** Slider divider position as a percentage from 0 to 100. */
  comparisonSliderPosition = 'cmpPos',

  /**
   * Array of active discussions ids (used in asset modal) - there may be more than one if clicked on timestamp marker
   */
  discussionId = 'dId',
  /** checkout plan name */
  planId = 'planId',
  /** checkout coupon code */
  couponCode = 'couponCode',
  /** checkout credit pack quantity */
  creditPackQuantity = 'creditPackQuantity',
  /** boolean for whether password was reset */
  passwordReset = 'passwordReset',
  canvasUploadDataUri = 'canvasUploadDataUri',
  canvasName = 'canvasName',
  /** Skill onboarding deep-link: a skill `slashCommand` (e.g. "remove-background") to invoke automatically. */
  skillName = 'skillName',
  /** Skill onboarding deep-link: a key into SKILL_ONBOARDING_IMAGES for the pre-baked image to upload. */
  imageName = 'imageName',
  redirect = 'redirect',
  skipOnboarding = 'skipOnboarding',
  existingEmail = 'existingEmail',
  /** indicates 'success' or 'failure' of slack intetgration auth flow */
  slackAuthStatus = 'slackAuthStatus',
  /** id of asset to open in Finder, used in open-desktop-file page */
  assetId = 'assetId',
  /** id of clip to open in Finder, used in open-desktop-file page */
  clipId = 'clipId',
  /** id of board to open in Finder, used in open-desktop-file page */
  boardId = 'boardId',
  /** controls visibility of notifications panel */
  notificationsPanel = 'nPanel',
  /** Workspace agent panel: currently open chat (agent session id). */
  chatId = 'chatId',
  /** Workspace agent panel: currently open session artifact id in the inline artifact view. */
  artifactId = 'artifactId',
  /** Workspace agent panel: open + preload a hero use case from a marketing deep link (e.g. `bulk-rename`). */
  agentUseCase = 'agentUseCase',
  /**
   * Role sent from the existing-user intro. Opens the workspace agent on the time-sink question.
   * A known role label, for example `Creative`.
   */
  onboardingRole = 'onboardingRole',
  /**
   * Time sinks sent with `onboardingRole`. Comma-separated ids, for example `collaboration,find`.
   */
  onboardingTimeSinks = 'onboardingTimeSinks',
  /** id of currently selected captured text */
  capturedTextId = 'capTextId',

  /** selected face on people tab */
  faceId = 'faceId',

  /** selected clip-person row on people tab (mutually exclusive with faceId) */
  clipPersonId = 'clipPersonId',
  /**
   * used for pdf pages
   */
  page = 'page',

  preview = 'preview',

  /** used on people page, can have values: named/unnamed */
  peopleType = 'pType',

  /** used on recently-deleted page, can have values: asset/board/library */
  deletedItemType = 'type',

  sortField = 'sortBy',
  sortDirection = 'sortDir',

  /* If set to true, the page opens intercom window */
  openChat = 'openChat',

  /**
   * Array of expanded sections in asset modal's info panel
   */
  expandedSections = 'expandedSections',

  dateType = 'dateType',
  startDate = 'startDate',
  endDate = 'endDate',
  campaignIds = 'cIds',
  adSetIds = 'asIds',
  showChart = 'showChart',
  /** id of ad to open in AdDetailsSidebar */
  adId = 'adId',
  /** Array of ad effective statuses to filter by */
  adStatuses = 'adStatuses',
  /** controls visibility of creator upgrade modal */
  creatorModal = 'creatorModal',
  /** controls visibility of demo form modal */
  demoModal = 'demoModal',
  /** id of import to open in import detail panel */
  importId = 'importId',
}

export const QueryParamsWithArrayValue = [
  QueryParamNames.campaignIds,
  QueryParamNames.adSetIds,
  QueryParamNames.adStatuses,
] as const;

export const CustomFieldFilters = [
  FilterParamNames.cfIdAnd,
  FilterParamNames.cfIdOr,
  FilterParamNames.cfIdNot,
  FilterParamNames.cfTextIs,
  FilterParamNames.cfTextPrefix,
  FilterParamNames.cfTextOr,
  FilterParamNames.cfTextNot,
  FilterParamNames.cfNotSet,
  FilterParamNames.cfDateStart,
  FilterParamNames.cfDateEnd,
];

export type FiltersWithSingleValueType =
  | FilterParamNames.recordedDateStart
  | FilterParamNames.recordedDateEnd
  | FilterParamNames.uploadedDateStart
  | FilterParamNames.uploadedDateEnd
  | FilterParamNames.modifiedDateStart
  | FilterParamNames.modifiedDateEnd
  | FilterParamNames.bookmarked
  | FilterParamNames.isOnBoards
  | FilterParamNames.hasOpenComments
  | FilterParamNames.hasMultipleVersions
  | FilterParamNames.untagged
  | FilterParamNames.libraryId
  | FilterParamNames.onlyInLibraries
  | FilterParamNames.boardId
  | FilterParamNames.takenDateStart
  | FilterParamNames.takenDateEnd
  | FilterParamNames.cameraMake
  | FilterParamNames.colorThreshold
  | FilterParamNames.similarToImage
  | FilterParamNames.similarTo;

export const FiltersWithSingleValue: FiltersWithSingleValueType[] = [
  FilterParamNames.recordedDateStart,
  FilterParamNames.recordedDateEnd,
  FilterParamNames.uploadedDateStart,
  FilterParamNames.uploadedDateEnd,
  FilterParamNames.modifiedDateStart,
  FilterParamNames.modifiedDateEnd,
  FilterParamNames.bookmarked,
  FilterParamNames.isOnBoards,
  FilterParamNames.onlyInLibraries,
  FilterParamNames.hasOpenComments,
  FilterParamNames.hasMultipleVersions,
  FilterParamNames.untagged,
  FilterParamNames.libraryId,
  FilterParamNames.boardId,
  FilterParamNames.takenDateStart,
  FilterParamNames.takenDateEnd,
  FilterParamNames.cameraMake,
  FilterParamNames.colorThreshold,
  FilterParamNames.similarToImage,
  FilterParamNames.similarTo,
];

export const FiltersWithBooleanValue: FilterParamNames[] = [
  FilterParamNames.bookmarked,
  FilterParamNames.isOnBoards,
  FilterParamNames.hasOpenComments,
  FilterParamNames.hasMultipleVersions,
  FilterParamNames.untagged,
];

export type FiltersWithArrayValueType =
  | FilterParamNames.tagIdOr
  | FilterParamNames.tagIdAnd
  | FilterParamNames.tagIdNot
  | FilterParamNames.smartTagIdAnd
  | FilterParamNames.smartTagIdOr
  | FilterParamNames.smartTagIdNot
  | FilterParamNames.source
  | FilterParamNames.type
  | FilterParamNames.color
  | FilterParamNames.uploaderId
  | FilterParamNames.ext
  | FilterParamNames.country
  | FilterParamNames.city
  | FilterParamNames.state
  | FilterParamNames.importedKeywordAnd
  | FilterParamNames.importedKeywordOr
  | FilterParamNames.importedKeywordNot
  | FilterParamNames.copyright
  | FilterParamNames.creator
  | FilterParamNames.cameraModel
  | FilterParamNames.orientation
  | FilterParamNames.videoFrameRate
  | FilterParamNames.videoAspectRatio
  | FilterParamNames.audioCoding
  | FilterParamNames.audioSampleRate
  | FilterParamNames.personIdAnd
  | FilterParamNames.personIdOr
  | FilterParamNames.locationBoards
  | FilterParamNames.locationLibraries
  | FilterParamNames.assetFileKind;

export const FiltersWithArrayValue: FiltersWithArrayValueType[] = [
  FilterParamNames.tagIdOr,
  FilterParamNames.tagIdAnd,
  FilterParamNames.tagIdNot,
  FilterParamNames.smartTagIdAnd,
  FilterParamNames.smartTagIdOr,
  FilterParamNames.smartTagIdNot,
  FilterParamNames.source,
  FilterParamNames.type,
  FilterParamNames.color,
  FilterParamNames.uploaderId,
  FilterParamNames.ext,
  FilterParamNames.importedKeywordAnd,
  FilterParamNames.importedKeywordOr,
  FilterParamNames.importedKeywordNot,
  FilterParamNames.copyright,
  FilterParamNames.creator,
  FilterParamNames.cameraModel,
  FilterParamNames.orientation,
  FilterParamNames.videoFrameRate,
  FilterParamNames.country,
  FilterParamNames.city,
  FilterParamNames.state,
  FilterParamNames.videoAspectRatio,
  FilterParamNames.audioCoding,
  FilterParamNames.audioSampleRate,
  FilterParamNames.personIdAnd,
  FilterParamNames.personIdOr,
  FilterParamNames.locationBoards,
  FilterParamNames.locationLibraries,
  FilterParamNames.assetFileKind,
];
